import { Sim } from '../../game/simulation';
import { t } from '../strings';
import { ICON } from '../icons';

/**
 * Unified plot HUD card — pinned at top-center while the player is in
 * plot mode. Replaces the old separate plot-aggregate progress bars and
 * the storage-bar plot overview with one card showing:
 *
 *   • Plot name + tile count + weed count (compact header).
 *   • Three big progress bars (till / water / seed) with %, current/total.
 *
 * Reads from Sim.plotTill/Water/SeedProgress every frame; hidden in
 * overview / zones-mode. plotAutoCompleted bursts the matching bar.
 */
type Mode = 'till' | 'water' | 'seed';

const MODE_LABEL: Record<Mode, string> = {
  till: 'Hoed', water: 'Watered', seed: 'Seeded',
};

export class PlotPanel {
  private root: HTMLDivElement;
  private bars: Record<Mode, { el: HTMLDivElement; fillEl: HTMLDivElement; labelEl: HTMLSpanElement; lastFrac: number }>;
  private lastHeaderHash = '';

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-plotpanel';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="ui-plotpanel__head">
        <span class="ui-plotpanel__icon glyph">${ICON.plot}</span>
        <span class="ui-plotpanel__name" data-field="name">—</span>
        <span class="ui-plotpanel__meta" data-field="meta">—</span>
      </div>
      <div class="ui-plotpanel__bars">
        ${(['till', 'water', 'seed'] as Mode[]).map(m => `
          <div class="ui-plotbar ui-plotbar--${m}" data-mode="${m}">
            <div class="ui-plotbar__fill"></div>
            <span class="ui-plotbar__label">${MODE_LABEL[m]} —</span>
          </div>
        `).join('')}
      </div>
    `;
    parent.appendChild(this.root);
    this.bars = { till: this.barRefs('till'), water: this.barRefs('water'), seed: this.barRefs('seed') };

    sim.bus.on(e => {
      const anyEvt = e as { type: string; mode?: Mode };
      if (anyEvt.type === 'plotAutoCompleted' && anyEvt.mode) {
        this.burst(anyEvt.mode);
      }
    });
  }

  private barRefs(mode: Mode) {
    const el = this.root.querySelector(`[data-mode="${mode}"]`) as HTMLDivElement;
    return {
      el,
      fillEl: el.querySelector('.ui-plotbar__fill') as HTMLDivElement,
      labelEl: el.querySelector('.ui-plotbar__label') as HTMLSpanElement,
      lastFrac: -1,
    };
  }

  private burst(mode: Mode) {
    const bar = this.bars[mode];
    if (!bar) return;
    bar.el.classList.remove('ui-plotbar--burst');
    void bar.el.offsetWidth;
    bar.el.classList.add('ui-plotbar--burst');
  }

  update(_dt: number) {
    const idx = this.sim.state.activePlotIdx;
    const visible = !!this.sim.state.plotInView && idx != null;
    if (this.root.hidden !== !visible) this.root.hidden = !visible;
    if (!visible) return;
    const plot = this.sim.state.plots[idx!];
    if (!plot) return;
    const tileCount = plot.tiles.length;
    const tileSet = new Set(plot.tiles.map(p => `${p.c}:${p.r}`));
    const weeds = this.sim.state.weeds.reduce(
      (n, w) => n + (tileSet.has(`${w.c}:${w.r}`) ? 1 : 0), 0);
    const name = (plot as any).name ?? `Plot ${idx! + 1}`;
    const headerHash = `${name}|${tileCount}|${weeds}`;
    if (headerHash !== this.lastHeaderHash) {
      this.lastHeaderHash = headerHash;
      this.setField('name', name);
      this.setField('meta', `${tileCount} tile${tileCount === 1 ? '' : 's'} · ${weeds} weed${weeds === 1 ? '' : 's'}`);
    }
    const progress: Record<Mode, { current: number; total: number; fraction: number }> = {
      till:  this.sim.plotTillProgress(idx!),
      water: this.sim.plotWaterProgress(idx!),
      seed:  this.sim.plotSeedProgress(idx!),
    };
    for (const mode of ['till', 'water', 'seed'] as Mode[]) {
      const bar = this.bars[mode];
      const p = progress[mode];
      if (Math.abs(p.fraction - bar.lastFrac) < 0.005) continue;
      bar.lastFrac = p.fraction;
      const pct = Math.round(p.fraction * 100);
      bar.fillEl.style.width = `${pct}%`;
      bar.labelEl.textContent = p.total > 0
        ? `${MODE_LABEL[mode]} ${pct}% (${p.current}/${p.total})`
        : `${MODE_LABEL[mode]} —`;
    }
  }

  private setField(field: string, text: string) {
    const el = this.root.querySelector(`[data-field="${field}"]`);
    if (el) el.textContent = text;
  }
}

void t; // strings table reserved for future i18n of the header
